<template>
    <div id="wkd-container">
        <form @submit="search">
            <div class="wkd-input-group">
                <label for="wkd-email" class="hide">Email</label>
                <input v-model="email" id="wkd-email" type="email" placeholder="email address" required>
                <button type="submit"><i class="wkd-icon wkd-search"/> Search key</button>
            </div>
        </form>
        <div v-if="key !== null" class="wkd-animate wkd-key-container">
            <div class="wkd-action-bar">
                <div class="wkd-tooltip">
                    <button @click="copy" @mouseout="clearTooltip">
                        <span class="wkd-tooltiptext" id="wkd-copy-tooltip"
                              ref="wkdCopyTooltip">Copy to clipboard</span>
                        <i class="wkd-icon wkd-copy"/> Copy
                    </button>
                </div>
                <button @click="download"><i class="wkd-icon wkd-download"/> Download</button>
            </div>
            <label for="wkd-key" class="hide"> Gpg key</label>
            <textarea class="wkd-key" id="wkd-key" v-model="key" ref="wkdKey"/>
        </div>
        <div v-if="notification !== null" class="wkd-animate">
            {{notification}}
        </div>
    </div>
</template>

<script>
  import base32 from '../plugins/base32';
  import Jssha from 'jssha';

  export default {
    name: "Wkd",
    data() {
      return {
        email: '',
        key: null,
        isSearching: false,
        hash: null,
        notification: null,
      }
    },
    methods: {
      /**
       * Copy key to clipboard
       */
      copy() {
        const copyText = this.$refs.wkdKey;
        copyText.select();
        /*For mobile devices*/
        copyText.setSelectionRange(0, 99999);
        document.execCommand("copy");
        window.getSelection().removeAllRanges();
        this.$refs.wkdCopyTooltip.innerHTML = `Copied key for ${this.email} to clipboard`;
      },
      /**
       * Clear the tooltip
       **/
      clearTooltip() {
        this.$refs.wkdCopyTooltip.innerHTML = 'Copy to clipboard';
      },

      /**
       * Download a key
       **/
      download() {
        const element = document.createElement('a');
        element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(this.key));
        element.setAttribute('download', `${this.hash}.asc`);
        element.style.display = 'none';
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
      },
      /**
       * Search a specific key
       * @param event
       */
      async search(event) {
        event.preventDefault();
        if (this.isValidEmail(this.email) && !this.isSearching) {
          this.reset();
          const emailChunks = this.email.split('@');
          const userhandel = emailChunks[0];
          const domain = emailChunks[1];
          this.hash = this.calcHash(userhandel);
          this.startSearching();
          // fetch key through php
          try {
            const localUrl = `/wkd.php?domain=${domain}&hash=${this.hash}`;
            await this.fetch(localUrl);
          } catch (errSimple) {
            this.endSearching();
            this.showNotification('Key not found');
          }
        } else {
          this.endSearching();
          this.showNotification('No valid email');
        }
      },
      /**
       * Fetch a url and handle success event
       **/
      async fetch(url) {
        const response = await fetch(url);
        if(response.ok) {
          this.endSearching();
          this.updateKey(response);
        } else {
          throw new Error('Not found');
        }
      },
      /**
       * Updates the Key
       **/
      updateKey(response) {
        response.text().then((text) => {
          this.key = text;
        });
      },
      /**
       * Reset state
       **/
      reset() {
        this.notification = null;
        this.hash = null;
        this.key = null;
        this.isSearching = false;
      },
      /**
       * Check if emailadress is valid
       * @param email
       * @return {boolean}
       */
      isValidEmail(email) {
        // eslint-disable-next-line no-useless-escape
        return /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(email);
      },
      /**
       * Calculate a base32 hash for the email-handle
       * @param userHandle
       * @return {Buffer}
       */
      calcHash(userHandle) {
        const shaObj = new Jssha("SHA-1", "TEXT");
        shaObj.update(userHandle.toLowerCase());
        return base32.encode(shaObj.getHash("ARRAYBUFFER"))
      },
      /**
       * Start the search
       **/
      startSearching() {
        this.isSearching = true;
        this.showNotification('...searching key');
        this.$emit('startSearching', this.email);
      },
      /**
       * End the search
       **/
      endSearching() {
        this.isSearching = false;
        this.resetNotification();
        this.$emit('endSearching');
      },
      /**
       * Show a notification
       **/
      showNotification(message) {
        this.notification = message;
      },
      /**
       * Reset notification
       **/
      resetNotification() {
        this.notification = null;
      }
    }
  }
</script>

<style scoped>
    /** General **/
    .hide {
        display: none;
    }

    .wkd-animate {
        animation: fadeIn;
        animation-duration: 600ms;
        margin-top: 0.75em;
    }

    input,
    button {
        font-size: 1em;
        line-height: 1.5em;
        padding: 0.1em;
    }

    button {
        display: flex;
        align-items: center;
        padding: 0.2em 0.4em;
    }

    textarea {
        font-family: monospace;
        font-size: 0.8em;
        line-height: 1.2em;
        text-align: left;
        min-height: 37.5em;
        width: calc(100% - 0.375em);
        max-width: 100%;
        padding: 0.125em;
        border: 0.0625em solid #ddd;
    }

    @keyframes fadeIn {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }

    /** icon **/
    .wkd-icon {
        width: 1em;
        height: 1em;
        display: inline-block;
        margin-right: 0.2em;
        background-size: contain;
    }

    .wkd-search {
        background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-search"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>') center center no-repeat;
    }

    .wkd-copy {
        background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg"  viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-clipboard"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path><rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect></svg>') center center no-repeat;
    }

    .wkd-download {
        background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg"  viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-download"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>') center center no-repeat;
    }

    /** Form **/
    .wkd-input-group {
        display: flex;
        flex-direction: row;
    }

    .wkd-input-group input[type="email"] {
        width: 75%
    }

    .wkd-input-group button {
        width: 25%;
        min-width: 11.2em;
    }

    /** Tooltip **/
    .wkd-tooltip {
        position: relative;
        display: inline-block;
    }

    .wkd-tooltip .wkd-tooltiptext {
        visibility: hidden;
        width: 140px;
        background-color: #555;
        color: #fff;
        text-align: center;
        border-radius: 6px;
        padding: 5px;
        position: absolute;
        z-index: 1;
        bottom: 150%;
        left: 50%;
        margin-left: -75px;
        opacity: 0;
        transition: opacity 0.3s;
    }

    .wkd-tooltip .wkd-tooltiptext::after {
        content: "";
        position: absolute;
        top: 100%;
        left: 50%;
        margin-left: -5px;
        border-width: 5px;
        border-style: solid;
        border-color: #555 transparent transparent transparent;
    }

    .wkd-tooltip:hover .wkd-tooltiptext {
        visibility: visible;
        opacity: 1;
    }

    .wkd-key-container {
        max-width: 100%;
    }

    .wkd-action-bar {
        display: flex;
        flex-direction: row;
        justify-content: flex-end;
    }
</style>
